import { useState } from 'react';
import { Field } from '../../components/Field.js';
import type { Action, OnboardingState } from './state.js';

const SUGGESTIONS = ['Chez Mamie', 'Chez Papa', 'Chez Maman', 'Maison de famille', 'Chez Papi'];
const RELATIONS = [
  'Fils / Fille',
  'Petit·e-enfant',
  'Neveu / Nièce',
  'Ami·e proche',
  'Aidant·e professionnel·le',
  'Autre',
];

interface Props {
  state: OnboardingState;
  dispatch: React.Dispatch<Action>;
  onNext: () => void;
  onBack: () => void;
}

export function Step2Foyer({ state, dispatch, onNext }: Props) {
  const [nom, setNom] = useState(state.foyerNom);
  const [senior, setSenior] = useState(state.seniorPrenom);
  const [relation, setRelation] = useState(state.relation || RELATIONS[0] || '');

  function handleNext() {
    dispatch({ type: 'SET_FOYER', foyerNom: nom, seniorPrenom: senior, relation });
    onNext();
  }

  const canNext = nom.trim().length > 0 && senior.trim().length > 0;

  return (
    <div
      className="container"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}
    >
      <div style={{ position: 'sticky', top: '160px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>
          Étape 1 — Foyer
        </div>
        <h1
          className="display"
          style={{ fontSize: 'clamp(42px, 4.5vw, 64px)', margin: '0 0 20px' }}
        >
          Comment s'appelle <em>le foyer</em> ?
        </h1>
        <p
          className="serif"
          style={{
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: '18px',
            lineHeight: 1.4,
            color: 'var(--ink-soft)',
            marginBottom: '24px',
          }}
        >
          Ce nom apparaît dans l'app famille. Votre proche ne le voit pas.
        </p>
        <div
          style={{
            background: 'var(--paper-2)',
            border: '1px solid var(--hairline-soft)',
            borderLeft: '3px solid var(--terracotta)',
            borderRadius: '0 8px 8px 0',
            padding: '16px 20px',
          }}
        >
          <p className="small" style={{ margin: 0 }}>
            <b>Pourquoi ?</b> Pour que chaque proche voit dans l'app « Chez Mamie » plutôt qu'un
            identifiant technique.
          </p>
        </div>
      </div>

      <div className="card">
        <Field
          id="foyer-nom"
          label="Nom du foyer"
          type="text"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="ex. Chez Mamie"
          autoFocus
        />
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '-8px',
            marginBottom: '20px',
          }}
        >
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setNom(s)}
              style={{
                fontSize: '12.5px',
                padding: '5px 12px',
                borderRadius: '999px',
                border: '1px solid var(--hairline)',
                background: nom === s ? 'var(--terracotta-soft)' : 'var(--paper)',
                color: 'var(--ink-soft)',
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        <Field
          id="senior-prenom"
          label="Prénom de votre proche"
          type="text"
          value={senior}
          onChange={(e) => setSenior(e.target.value)}
          placeholder="ex. Colette"
          hint="Affiché sur l'écran : « Bonjour Colette »."
        />

        <Field
          id="relation"
          label="Votre relation"
          as="select"
          value={relation}
          onChange={(e) => setRelation(e.target.value)}
        >
          {RELATIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Field>

        <button
          type="button"
          className="btn btn-primary btn-lg"
          style={{ width: '100%', marginTop: '8px' }}
          disabled={!canNext}
          onClick={handleNext}
        >
          Continuer
        </button>
      </div>
    </div>
  );
}
